import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes, ReactNode } from 'react';
import { Close } from '@tcl-argon-icons/icons';
import { InputTheme } from '@tcl-argon-models/models';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { useAlertChildrenStyles, useAlertStyles } from './styles';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type AlertChildObject = {
  icon?: ReactNode;
  title?: ReactNode;
  content: ReactNode;
  action?: ReactNode;
};

const isAlertChildObject = (children: unknown): children is AlertChildObject => {
  return 'content' in (children as AlertChildObject);
};

export interface AlertChildrenProps {
  children: AlertChildObject;
  theme: InputTheme;
  isDismissable: boolean;
}

const AlertChildren: React.FC<AlertChildrenProps> = ({
  children: { icon, title, content, action },
  isDismissable,
  theme,
}) => {
  const classes = useAlertChildrenStyles({ isDismissable, theme, children: { action, content, title, icon } });

  return (
    <>
      {icon && <div className={classes.icon}>{icon}</div>}
      {title && <div className={classes.title}>{title}</div>}
      <div className={classes.content}>{content}</div>
      {action && <div className={classes.action}>{action}</div>}
      {isDismissable && <div className={classes.dismissButtonSpacer}></div>}
    </>
  );
};

export interface AlertProps extends DivElement {
  /**
   * Accepts anything normally acceptable as a React child. Also accepts an object
   * as a child. When an object is passed, design-approved styling is automatically
   * applied to the different elements. See the source code for the Alert design docs
   * for an example
   */
  children: AlertChildObject | ReactNode;
  classes?: Record<string, string>;
  /**
   * If `true`, display an "X" button. If a user clicks the "X" button,
   * the `onDismiss` handler will be triggered.
   */
  isDismissable?: boolean;
  /** Triggered when a user clicks the "X" button of a dismissable alert. */
  onDismiss?: () => void;
  /**
   * A theme that be used to style an Alert's text, background, and icon colors.
   * Affects styles only.
   */
  theme?: InputTheme;
}

export const Alert: React.FC<AlertProps> = ({
  children,
  className,
  classes: classOverrides,
  isDismissable = false,
  onDismiss = () => {},
  theme = InputTheme.Default,
  ...props
}) => {
  const classes = mergeClasses(useAlertStyles({ theme, children }), classOverrides, { root: className });

  return (
    <div className={classes.root} data-analytics={`${theme}-alert`} {...props}>
      {isAlertChildObject(children) ? (
        <AlertChildren {...{ isDismissable, theme }}>{children}</AlertChildren>
      ) : (
        <div>{children}</div>
      )}
      {isDismissable && (
        <button
          type="button"
          className={classes.dismissButton}
          onClick={onDismiss}
          name="dismiss"
          aria-label="Dismiss this alert"
        >
          <Close aria-hidden="true" height="15px" width="15px" />
        </button>
      )}
    </div>
  );
};
